import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { useState } from 'react';
import ErrorBoundary from 'src/components/ErrorBoundary';
import DoctorConfirmation from 'src/interviews/components/DoctorConfirmation';
import { isDoctorConfirmationAnOption } from 'src/interviews/components/DoctorConfirmation/utils';
import useInterview from 'src/interviews/hooks/useInterview';
import useInterviewType from 'src/interviews/hooks/useInterviewType';
import EmailTranscriptCard from '../EmailTranscriptCard';
import RelatedQuestions from '../RelatedQuestions';
import RelatedReading from '../RelatedReading';
import TreatmentsForYou from '../TreatmentsForYou';
import { RichEndingContext } from './useRichEndingContext';

import type { PropsWithChildren } from 'react';
import type {
  IInterviewResults,
  InterviewSetting,
  ITopProduct,
  MiniInterviewType,
  TreatmentSupplier,
} from 'src/packages/polaris/types';
import type { RelatedReading as RelatedReadingType } from './types';

type RichEndingsProps =
  | {
      interviewType: MiniInterviewType;
      products?: never;
      query?: never;
      result: IInterviewResults;
      isAlarmContinuation?: boolean;
      symptomInterview?: InterviewSetting;
    }
  | {
      interviewType: MiniInterviewType;
      products: ITopProduct[];
      query?: never;
      result?: never;
      isAlarmContinuation?: never;
      symptomInterview?: never;
    }
  | {
      interviewType: MiniInterviewType;
      products?: never;
      query: string;
      result?: never;
      isAlarmContinuation?: never;
      symptomInterview?: never;
    }
  | {
      interviewType: MiniInterviewType;
      products: ITopProduct[];
      query: string;
      result?: never;
      isAlarmContinuation?: never;
      symptomInterview?: never;
    };

export const Wrapper = ({
  children,
  component,
}: PropsWithChildren<{
  component: string;
}>) => {
  const { token } = useInterview();
  const interviewType = useInterviewType();
  return (
    <ErrorBoundary
      fallback={null}
      component={component}
      interviewToken={token}
      interviewType={interviewType}
    >
      <Grid item className="!bp-my-2">
        <Divider />
      </Grid>
      {children}
    </ErrorBoundary>
  );
};

function RichEnding(props: RichEndingsProps) {
  const { interviewType } = props;

  const { token: interviewToken } = useInterview();
  const [relatedReadings, setRelatedReadings] = useState<RelatedReadingType[]>();
  const [treatmentSuppliers, setTreatmentSuppliers] = useState<TreatmentSupplier[]>();

  return (
    <RichEndingContext.Provider
      value={{
        relatedReadings,
        setRelatedReadings,
        setTreatmentPlan: () => {},
        setTreatmentSuppliers,
        treatmentSuppliers,
      }}
    >
      <Grid container direction="column" spacing={2}>
        {/* OptimalMD Offering */}
        {isDoctorConfirmationAnOption(interviewType) && (
          <DoctorConfirmation
            interviewToken={interviewToken || ''}
            interviewType={interviewType}
            isAlarmingDx={Boolean(props.result?.alarm)}
          />
        )}

        <RelatedQuestions {...props} />
        <TreatmentsForYou {...props} />
        <RelatedReading {...props} />
        <EmailTranscriptCard interviewType={interviewType} />
      </Grid>

      <div className="pb-4" />
    </RichEndingContext.Provider>
  );
}

export default RichEnding;
export type { RichEndingsProps };
